import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { RefreshCw, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { EducationalDialog } from './EducationalDialog';

export const RefinanceComparisonCard: React.FC = () => {
  const [showDialog, setShowDialog] = useState(false);

  return (
    <>
      <Card className="h-full bg-card border-border">
        <CardHeader className="pb-2 md:pb-3">
          <CardTitle className="text-sm md:text-base flex items-center gap-1.5 md:gap-2">
            <RefreshCw className="w-3.5 h-3.5 md:w-4 md:h-4 text-primary flex-shrink-0" />
            <span className="hidden sm:inline">Cash-Out Refinance vs. HELOC</span>
            <span className="sm:hidden">Refi vs. HELOC</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 md:space-y-3">
          <p className="text-xs md:text-sm text-muted-foreground">
            Two ways to access home equity.
          </p>

          <div className="overflow-x-auto -mx-2 md:mx-0 px-2 md:px-0 scrollbar-thin">
            <div className="min-w-[400px] md:min-w-0">
              <table className="w-full text-xs md:text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-1.5 md:py-2 font-semibold">Feature</th>
                    <th className="text-left py-1.5 md:py-2 font-semibold text-blue-600">Refi</th>
                    <th className="text-left py-1.5 md:py-2 font-semibold text-green-600">HELOC</th>
                  </tr>
                </thead>
                <tbody className="text-muted-foreground">
                  <tr className="border-b">
                    <td className="py-1.5 md:py-2">Access</td>
                    <td className="py-1.5 md:py-2">Lump sum</td>
                    <td className="py-1.5 md:py-2">Line of credit</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-1.5 md:py-2">Rate</td>
                    <td className="py-1.5 md:py-2">Fixed, lower</td>
                    <td className="py-1.5 md:py-2">Variable</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-1.5 md:py-2">Costs</td>
                    <td className="py-1.5 md:py-2">2-5%</td>
                    <td className="py-1.5 md:py-2">$0-$500</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-1.5 md:py-2">Term</td>
                    <td className="py-1.5 md:py-2">15-30 yr</td>
                    <td className="py-1.5 md:py-2">10-20 yr</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-1.5 md:py-2">Payment</td>
                    <td className="py-1.5 md:py-2">Replaces</td>
                    <td className="py-1.5 md:py-2">Additional</td>
                  </tr>
                  <tr>
                    <td className="py-1.5 md:py-2">Best For</td>
                    <td className="py-1.5 md:py-2">Large</td>
                    <td className="py-1.5 md:py-2">Ongoing</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="grid gap-1.5 md:gap-2 mt-2 md:mt-3">
            <div className="p-2 md:p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
              <h4 className="font-semibold text-xs md:text-sm mb-0.5 md:mb-1">Cash-Out Refi Use Cases</h4>
              <ul className="text-[10px] md:text-xs text-muted-foreground space-y-0.5 md:space-y-1">
                <li>• Major renovations ($50K+)</li>
                <li>• Debt consolidation</li>
                <li>• Lower your current rate</li>
                <li>• Investment property down</li>
              </ul>
            </div>

            <div className="p-2 md:p-3 rounded-lg bg-green-500/10 border border-green-500/20">
              <h4 className="font-semibold text-xs md:text-sm mb-0.5 md:mb-1">HELOC Use Cases</h4>
              <ul className="text-[10px] md:text-xs text-muted-foreground space-y-0.5 md:space-y-1">
                <li>• College tuition (pay as you go)</li>
                <li>• Home improvements over time</li>
                <li>• Emergency fund backup</li>
                <li>• Keep your low mortgage rate</li>
              </ul>
            </div>
          </div>

          <Button 
            variant="outline" 
            size="sm" 
            className="w-full h-8 text-xs md:text-sm"
            onClick={() => setShowDialog(true)}
          >
            <Info className="w-3 h-3 mr-1.5 md:mr-2" />
            <span className="hidden sm:inline">Detailed Comparison & Scenarios</span>
            <span className="sm:hidden">Learn More</span>
          </Button>
        </CardContent>
      </Card>

      <EducationalDialog
        open={showDialog}
        onClose={() => setShowDialog(false)}
        title="Complete Guide: Cash-Out Refinance vs. HELOC"
      >
        <div className="space-y-4">
          <section>
            <h4 className="font-semibold mb-2">How Cash-Out Refinance Works</h4>
            <p className="text-sm text-muted-foreground mb-2">
              You replace your existing mortgage with a new, larger loan and receive the difference in cash.
            </p>
            <p className="text-sm text-muted-foreground mb-2">
              <strong>Example:</strong> Current mortgage balance: $200K. Home value: $400K. 
              You refinance for $300K, pay off the $200K mortgage, and receive $100K cash (minus closing costs).
            </p>
            <p className="text-sm font-semibold mb-1">Pros:</p>
            <ul className="space-y-1 text-sm text-muted-foreground">
              <li>✓ Fixed interest rate (predictable payments)</li>
              <li>✓ Lower rates than HELOC or personal loans</li>
              <li>✓ Can lower your existing mortgage rate simultaneously</li>
              <li>✓ Tax deductible if used for home improvements</li>
            </ul>
            <p className="text-sm font-semibold mb-1 mt-2">Cons:</p>
            <ul className="space-y-1 text-sm text-muted-foreground">
              <li>✗ High closing costs (2-5% of loan amount)</li>
              <li>✗ Resets your mortgage term to 30 years</li>
              <li>✗ Not worth it if current rate is lower than market</li>
              <li>✗ Lengthier approval process (like original mortgage)</li>
            </ul>
          </section>

          <section>
            <h4 className="font-semibold mb-2">How HELOC Works</h4>
            <p className="text-sm text-muted-foreground mb-2">
              A revolving line of credit secured by your home equity. You borrow only what you need, when you need it.
            </p>
            <p className="text-sm text-muted-foreground mb-2">
              <strong>Two Phases:</strong>
            </p>
            <ul className="space-y-1 text-sm text-muted-foreground mb-2">
              <li>• <strong>Draw Period (10 years):</strong> Borrow as needed, pay interest only</li>
              <li>• <strong>Repayment Period (10-20 years):</strong> No more borrowing, pay principal + interest</li>
            </ul>
            <p className="text-sm font-semibold mb-1">Pros:</p>
            <ul className="space-y-1 text-sm text-muted-foreground">
              <li>✓ Low/no closing costs</li>
              <li>✓ Flexibility - borrow only what you need</li>
              <li>✓ Keeps your existing low mortgage rate intact</li>
              <li>✓ Interest only on amount borrowed</li>
            </ul>
            <p className="text-sm font-semibold mb-1 mt-2">Cons:</p>
            <ul className="space-y-1 text-sm text-muted-foreground">
              <li>✗ Variable interest rate (can increase)</li>
              <li>✗ Typically higher rates than cash-out refi</li>
              <li>✗ Payment shock when draw period ends</li>
              <li>✗ Home is collateral (risk of foreclosure)</li>
            </ul>
          </section>

          <section>
            <h4 className="font-semibold mb-2">Cost Example: $100K Equity Access</h4>
            <div className="bg-muted/50 p-4 rounded-lg space-y-3">
              <div>
                <p className="font-semibold text-sm">Cash-Out Refinance</p>
                <p className="text-sm text-muted-foreground">New loan: $300K at 6.5% (30 years)</p>
                <p className="text-sm text-muted-foreground">Closing costs: ~$9,000 (3% of $300K)</p>
                <p className="text-sm font-semibold">Monthly payment: ~$1,896</p>
                <p className="text-sm text-muted-foreground">Net cash received: $91,000</p>
              </div>
              <div className="border-t pt-3">
                <p className="font-semibold text-sm">HELOC</p>
                <p className="text-sm text-muted-foreground">Credit line: $100K at 8.5% variable</p>
                <p className="text-sm text-muted-foreground">Closing costs: ~$0-500</p>
                <p className="text-sm font-semibold">Additional monthly: ~$708 (interest only)</p>
                <p className="text-sm text-muted-foreground">Plus existing mortgage payment</p>
              </div>
            </div>
          </section>

          <section>
            <h4 className="font-semibold mb-2">Decision Framework</h4>
            <p className="text-sm font-semibold mb-1">Choose Cash-Out Refinance if:</p>
            <ul className="space-y-1 text-sm text-muted-foreground mb-2">
              <li>• You need a large lump sum ($50K+)</li>
              <li>• Current mortgage rate is high (can improve rate)</li>
              <li>• You want fixed, predictable payments</li>
              <li>• You're consolidating high-interest debt</li>
            </ul>
            <p className="text-sm font-semibold mb-1">Choose HELOC if:</p>
            <ul className="space-y-1 text-sm text-muted-foreground">
              <li>• You need flexibility (draw over time)</li>
              <li>• Current mortgage rate is excellent (don't want to lose it)</li>
              <li>• You want minimal closing costs</li>
              <li>• You're disciplined with credit (won't overspend)</li>
            </ul>
          </section>

          <section>
            <h4 className="font-semibold mb-2">Tax Considerations</h4>
            <p className="text-sm text-muted-foreground">
              <strong>Important:</strong> Under current tax law, mortgage interest (including cash-out refi and HELOC) 
              is only tax-deductible if the funds are used to "buy, build, or substantially improve" your home. 
              Using the funds for debt consolidation, education, or other purposes makes the interest non-deductible.
            </p>
          </section>
        </div>
      </EducationalDialog>
    </>
  );
};
